import { ArrowPathIcon } from '@heroicons/react/16/solid';
import {
  ArrowTopRightOnSquareIcon,
  InformationCircleIcon,
} from '@heroicons/react/20/solid';
import React, { useCallback, useState } from 'react';
import { toast } from 'sonner';

import { useMatterServicesWithCatalogue } from '@/components/services';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { useGetFirmQuery } from '@/services/api/firmsService';
import {
  useBulkUpdateMatterServiceWorkflowsMutation,
  useGetMatterDetailsQuery,
  usePatchMatterServiceMutation,
  useSyncMatterServicesFromFirmMutation,
} from '@/services/api/mattersService';
import { getApiErrorMessage } from '@/utils/api-error';

import { ServicesAccordionList } from '../matter-form/services';

interface MatterServicesTabProps {
  matterId: string;
}

/**
 * Services configuration tab for matter detail page.
 *
 * Architecture aligned with FirmServicesWorkflowsConfig:
 * - Uses service catalogue merged with matter config (like firm)
 * - Shows ALL services from catalogue
 * - Services without MatterService records shown as inactive
 * - Sends only changed workflow to API (not all workflows)
 * - Notes editing with async save
 */
export const MatterServicesTab: React.FC<MatterServicesTabProps> = ({
  matterId,
}) => {
  // Get firm ID from matter details
  const { data: matterDetails } = useGetMatterDetailsQuery(matterId);
  const firmId = matterDetails?.firm?.id ?? null;

  // Check feature flag
  const { data: firmData, isLoading: isFirmLoading } = useGetFirmQuery(
    firmId ?? '',
    { skip: !firmId },
  );
  const isMatterWorkflowsEnabled =
    firmData?.feature_flags?.matter_workflow_overrides === 'true';

  // Use merged catalogue + matter services (like firm pattern)
  const {
    services: displayServices,
    serviceIdToMatterServiceId,
    isLoading,
    error: servicesError,
    createMatterService,
    isCreating,
  } = useMatterServicesWithCatalogue(matterId, firmId);

  const [patchMatterService] = usePatchMatterServiceMutation();
  const [bulkUpdateWorkflows] = useBulkUpdateMatterServiceWorkflowsMutation();
  const [syncFromFirm, { isLoading: isSyncing }] =
    useSyncMatterServicesFromFirmMutation();

  // Reset confirmation dialog state
  const [showResetDialog, setShowResetDialog] = useState(false);

  /**
   * Handle reset to firm defaults.
   * Syncs matter services to match current firm configuration.
   */
  const handleResetToFirmDefaults = useCallback(async () => {
    try {
      const result = await syncFromFirm({ matterId }).unwrap();

      // Build a summary message
      const changes: string[] = [];
      if (result.services_created > 0) {
        changes.push(`${result.services_created} service(s) added`);
      }
      if (result.services_activated > 0) {
        changes.push(`${result.services_activated} service(s) activated`);
      }
      if (result.services_deactivated > 0) {
        changes.push(`${result.services_deactivated} service(s) deactivated`);
      }
      if (result.workflows_enabled > 0) {
        changes.push(`${result.workflows_enabled} workflow(s) enabled`);
      }
      if (result.workflows_disabled > 0) {
        changes.push(`${result.workflows_disabled} workflow(s) disabled`);
      }

      if (changes.length === 0) {
        toast.success('Already in sync with firm defaults');
      } else {
        toast.success(`Reset complete: ${changes.join(', ')}`);
      }

      setShowResetDialog(false);
    } catch (error) {
      toast.error(
        `Failed to reset to firm defaults: ${getApiErrorMessage(error)}`,
      );
    }
  }, [matterId, syncFromFirm]);

  /**
   * Get the current workflow state for a service.
   */
  const getWorkflowState = useCallback(
    (serviceId: string, workflowCode: string) => {
      const service = displayServices.find((s) => s.service_id === serviceId);
      return service?.workflows.find((w) => w.workflow_code === workflowCode);
    },
    [displayServices],
  );

  /**
   * Toggle service active state.
   * If service doesn't exist yet (no MatterService record), auto-create it first.
   */
  const handleServiceToggle = useCallback(
    async (serviceId: string, active: boolean) => {
      const matterServiceId = serviceIdToMatterServiceId.get(serviceId);

      if (!matterServiceId) {
        // Service doesn't exist yet - auto-create it (like firm pattern)
        if (!active) {
          // Toggling OFF a non-existent service is a no-op
          return;
        }

        try {
          await createMatterService({
            matterId,
            serviceId,
          }).unwrap();

          toast.success('Service added and activated successfully');
        } catch (error) {
          toast.error(`Failed to add service: ${getApiErrorMessage(error)}`);
        }
        return;
      }

      // Service exists - just toggle its active state
      try {
        await patchMatterService({
          matterId,
          matterServiceId,
          patch: { is_active: active },
        }).unwrap();

        toast.success(
          `Service ${active ? 'activated' : 'deactivated'} successfully`,
        );
      } catch (error) {
        toast.error(`Failed to update service: ${getApiErrorMessage(error)}`);
      }
    },
    [
      matterId,
      serviceIdToMatterServiceId,
      patchMatterService,
      createMatterService,
    ],
  );

  /**
   * Toggle workflow enabled state.
   * If service doesn't exist yet, auto-create it first (like auto-create on service toggle).
   * Sends only the changed workflow (matching firm pattern).
   */
  const handleWorkflowToggle = useCallback(
    async (serviceId: string, workflowCode: string, enabled: boolean) => {
      let matterServiceId = serviceIdToMatterServiceId.get(serviceId);

      // If service doesn't exist yet, auto-create it first
      if (!matterServiceId) {
        try {
          const created = await createMatterService({
            matterId,
            serviceId,
          }).unwrap();
          matterServiceId = created.id;
        } catch (error) {
          toast.error(`Failed to add service: ${getApiErrorMessage(error)}`);
          return;
        }
      }

      const currentWorkflow = getWorkflowState(serviceId, workflowCode);
      const currentNotes = currentWorkflow?.notes ?? '';

      try {
        await bulkUpdateWorkflows({
          matterId,
          matterServiceId,
          workflows: [
            {
              workflow_code: workflowCode,
              is_enabled: enabled,
              notes: currentNotes,
            },
          ],
        }).unwrap();

        toast.success(
          `Workflow ${enabled ? 'enabled' : 'disabled'} successfully`,
        );
      } catch (error) {
        toast.error(`Failed to update workflow: ${getApiErrorMessage(error)}`);
      }
    },
    [
      matterId,
      serviceIdToMatterServiceId,
      getWorkflowState,
      bulkUpdateWorkflows,
      createMatterService,
    ],
  );

  /**
   * Save workflow notes.
   * If service doesn't exist yet, auto-create it first.
   * Throws on error so NotesEditor can handle save failure UI.
   */
  const handleWorkflowNotesChange = useCallback(
    async (
      serviceId: string,
      workflowCode: string,
      notes: string,
    ): Promise<void> => {
      let matterServiceId = serviceIdToMatterServiceId.get(serviceId);

      // If service doesn't exist yet, auto-create it first
      if (!matterServiceId) {
        const created = await createMatterService({
          matterId,
          serviceId,
        }).unwrap();
        matterServiceId = created.id;
      }

      const currentWorkflow = getWorkflowState(serviceId, workflowCode);
      // For newly created services, default to disabled
      const isEnabled = currentWorkflow?.is_enabled ?? false;

      await bulkUpdateWorkflows({
        matterId,
        matterServiceId,
        workflows: [
          {
            workflow_code: workflowCode,
            is_enabled: isEnabled,
            notes,
          },
        ],
      }).unwrap();
    },
    [
      matterId,
      serviceIdToMatterServiceId,
      getWorkflowState,
      bulkUpdateWorkflows,
      createMatterService,
    ],
  );

  // Loading state (initial load only - isCreating is handled inline)
  if ((isLoading || isFirmLoading) && !isCreating) {
    return (
      <div className="space-y-3 p-6">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
      </div>
    );
  }

  // Feature flag not enabled - show "coming soon" message
  if (!isMatterWorkflowsEnabled) {
    return (
      <div className="flex h-96 flex-col items-center justify-center gap-3 p-6">
        <InformationCircleIcon className="h-12 w-12 text-zinc-300" />
        <div className="text-center">
          <p className="text-base font-medium text-zinc-600 dark:text-zinc-300">
            Scope of Work feature not yet live for this firm
          </p>
          <p className="mt-1 text-sm text-zinc-400 dark:text-zinc-500">
            Coming soon
          </p>
        </div>
      </div>
    );
  }

  // Error state
  if (servicesError) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-sm text-red-600 dark:text-red-400">
          Failed to load services. Please try again.
        </p>
      </div>
    );
  }

  // Empty state (no services in catalogue)
  if (displayServices.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-12">
        <InformationCircleIcon className="h-12 w-12 text-zinc-400" />
        <div className="text-center">
          <p className="text-lg font-medium text-zinc-700 dark:text-zinc-300">
            No services available
          </p>
          <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
            The service catalogue is empty.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-4 flex items-center justify-between gap-4">
        <div className="flex-1 min-w-0">
          <h2 className="text-sm font-semibold text-zinc-950 dark:text-white">
            Scope of Work
          </h2>
          <p className="mt-0.5 text-xs text-zinc-500 dark:text-zinc-400">
            Scope is inherited from firm settings. Add case guidelines per
            workflow or edit tasks directly to customize.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {firmId && (
            <a
              href={`/firms/${firmId}/services`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-200 transition-colors"
            >
              <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
              Edit firm scope of work
            </a>
          )}
          <button
            type="button"
            onClick={() => setShowResetDialog(true)}
            disabled={isSyncing}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-zinc-700 dark:text-zinc-300 bg-white dark:bg-zinc-800 border border-zinc-300 dark:border-zinc-600 rounded-md hover:bg-zinc-50 dark:hover:bg-zinc-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <ArrowPathIcon className="h-3.5 w-3.5" />
            Reset to firm scope
          </button>
        </div>
      </div>

      <ServicesAccordionList
        services={displayServices}
        onServiceToggle={handleServiceToggle}
        onWorkflowToggle={handleWorkflowToggle}
        onWorkflowNotesChange={handleWorkflowNotesChange}
        disabled={false}
        defaultCollapsed
      />

      {/* Reset confirmation dialog */}
      <AlertDialog open={showResetDialog} onOpenChange={setShowResetDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Reset to firm&apos;s default scope of work?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will sync all services and workflows to match the firm&apos;s
              current scope of work configuration. Your matter-specific workflow
              guidelines will be preserved.w
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isSyncing}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleResetToFirmDefaults}
              disabled={isSyncing}
            >
              {isSyncing ? 'Resetting...' : 'Reset'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
